<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
    title="Oracle Cloud VNコネクト一覧"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <!-- 回線情報 -->
            <div>
              <h3>回線情報</h3>
            </div>
            <v-row class="my-1 ml-3">
              <v-col cols="4">
                <app-label-group
                  label="回線番号"
                  :value="vnConnectList.enumber"
                />
              </v-col>
              <v-col cols="4">
                <app-label-group
                  label="アクセス回線メニュー"
                  :value="
                    $filter.enumTo(
                      vnConnectList.accessType,
                      'cloudVn_accessType'
                    ) +
                    '(' +
                    $filter.bandwidthTo(accessLineMenuBandwidth, 'Mbps') +
                    ')'
                  "
                />
              </v-col>
              <v-col cols="4">
                <app-label-group
                  label="事業所名"
                  :value="vnConnectList.office"
                />
              </v-col>
            </v-row>

            <!-- クラウド情報 -->
            <h3>クラウド情報</h3>
            <v-row class="mt-1 ml-3">
              <v-col cols="4">
                <app-label-group label="種別" value="Oracle Cloud" />
              </v-col>
              <v-col cols="4">
                <app-label-group label="帯域">
                  <template #value>
                    <span class="ml-3"
                      >{{
                        $filter.bandwidthTo(vnConnectList.bandwidth, "Mbps")
                      }}(設定可能帯域:</span
                    >
                    <span
                      :class="{
                        'text-error': remainingBandwidth.value < 0,
                      }"
                      >{{
                        $filter.bandwidthTo(remainingBandwidth, "Mbps")
                      }}</span
                    >)
                  </template>
                </app-label-group>
              </v-col>
            </v-row>

            <!-- VNコネクト情報 -->
            <div class="d-flex justify-space-between mb-4">
              <div class="d-flex align-end">
                <h3>VNコネクト情報</h3>
              </div>
              <div class="d-flex ga-2">
                <v-btn color="primary" @click="register" v-permission.config
                  >追加</v-btn
                >
                <v-btn
                  color="primary"
                  @click="modifyStatus(true)"
                  v-permission.config
                  >有効</v-btn
                >
                <v-btn
                  color="primary"
                  @click="modifyStatus(false)"
                  v-permission.config
                  >無効</v-btn
                >
              </div>
            </div>
            <v-alert
              v-if="alertMsg"
              class="my-2 text-left"
              :text="alertMsg"
            ></v-alert>
            <div v-permission.config class="text-left">
              <v-alert v-if="hasCloudVnResetting" class="my-2" color="red">{{
                $filter.message("vn_connect_has_resetting")
              }}</v-alert>
            </div>
            <app-table
              primary-key="cloudVnConnectAct.wnumber"
              :fields="vnConnects.fields"
              :items="vnConnectList.cloudVnConnectsList"
              :selectable="selectable"
              v-model="vnConnects.selected"
              :hover="true"
              v-model:table-sort-by="vnConnects.sortBy"
              @click:row="showConnectionDetails"
              :td-class="cellClass"
            >
              <template #cell(wnumber)="{ item }">
                {{ item.cloudVnConnectAct.wnumber }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.wnumber
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(vnConnectName)="{ item }">
                {{ item.cloudVnConnectAct.vnConnectName }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.vnConnectName
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(isVnConnectStatus)="{ item }">
                {{
                  $filter.enumTo(
                    item.cloudVnConnectAct.isVnConnectStatus,
                    "enable"
                  )
                }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  $filter.enumTo(
                    item.cloudVnConnectSby.isVnConnectStatus,
                    "enable"
                  )
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(serviceKey)="{ item }">
                {{ item.cloudVnConnectAct.serviceKey }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.serviceKey
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(bandwidth)="{ item }">
                {{ item.cloudVnConnectCommon.bandwidthType }}({{
                  $filter.bandwidthTo(item.cloudVnConnectCommon.bandwidth)
                }})
              </template>
              <template #cell(routingType)="{ item }">
                {{ item.cloudVnConnectCommon.routingType }}
              </template>
              <template #cell(description)="{ item }">
                {{
                  $filter.emptyTo(item.cloudVnConnectCommon.description, "-")
                }}
              </template>
            </app-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { AppTableHeader, SortItem } from "@/components/AppTable.vue";
import type { CloudVnOracleList } from "@/apis/CloudVnOracleApi";
import MultiCloudOracleVnConnectionReference from "@/modals/multicloud/oracle/MultiCloudOracleVnConnectionReference.vue";
import MultiCloudVNConnectStatusModify from "@/modals/multicloud/MultiCloudVNConnectStatusModify.vue";
import type { Line } from "@/apis/CloudLineApi";
import MultiCloudOracleVnLineEdit from "@/modals/multicloud/oracle/MultiCloudOracleVnLineEdit.vue";

type CloudVnOracleItem = CloudVnOracleList["cloudVnConnectsList"][0];

export default defineComponent({
  name: "MultiCloudOracleVnConnectionList",
  props: {
    /** クラウド回線SEQ */
    cloudLineSeq: {
      type: String as PropType<string>,
      required: true,
    },
    /** 該当クラウドメニューの設定未完了VNコネクト有無 */
    hasCloudVnResetting: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    /** アクセス回線メニューの帯域 */
    accessLineMenuBandwidth: {
      type: Object as PropType<Line["bandwidth"]>,
      required: true,
    },
  },
  data() {
    return {
      vnConnectList: {} as CloudVnOracleList,
      vnConnects: {
        sortBy: { key: "wnumber", order: "desc" } as SortItem,
        fields: [
          {
            key: "wnumber",
            title: "VNコネクト番号(Act/Sby)",
            width: "150px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnOracleItem) => {
              return item.cloudVnConnectAct.wnumber;
            },
          },
          {
            key: "vnConnectName",
            title: "VNコネクト名(Act/Sby)",
            width: "140px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnOracleItem) => {
              return item.cloudVnConnectAct.vnConnectName;
            },
          },
          {
            key: "isVnConnectStatus",
            title: "有効/無効(Act/Sby)",
            width: "110px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnOracleItem) => {
              return this.$filter.enumTo(
                item.cloudVnConnectAct.isVnConnectStatus,
                "enable"
              );
            },
          },
          {
            key: "serviceKey",
            title: "OCID(Act/Sby)",
            width: "180px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnOracleItem) => {
              return item.cloudVnConnectAct.serviceKey;
            },
          },
          {
            key: "vpnVnCode",
            title: "接続先VPN/VNコード",
            width: "185px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnOracleItem) => {
              if (item.cloudVnConnectCommon.vpnVnCode.startsWith("D")) {
                return `${item.cloudVnConnectCommon.vpnVnCode}(
                  ${item.cloudVnConnectCommon.vnType}
                )`;
              }
              return item.cloudVnConnectCommon.vpnVnCode;
            },
          },
          {
            key: "bandwidth",
            title: "品目",
            width: "160px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnOracleItem) => {
              return this.$filter.bandwidthToBps(
                item.cloudVnConnectCommon.bandwidth
              );
            },
          },
          { key: "routingType", title: "ルーティング種別", width: "155px" },
          { key: "description", title: "備考", width: "200px" },
        ] as AppTableHeader<CloudVnOracleList>[],
        selected: [] as CloudVnOracleList["cloudVnConnectsList"],
      },
      alertMsg: null as string | null,
      isLoaded: false,
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
    /** 選択済みのW番号一覧. SbyがnullのW番号は一覧に含まれない */
    selectedWNumbers(): string[] {
      return this.vnConnects.selected.flatMap((e) => {
        return e.cloudVnConnectSby
          ? [e.cloudVnConnectAct.wnumber, e.cloudVnConnectSby.wnumber]
          : [e.cloudVnConnectAct.wnumber];
      });
    },
    /** 残りの設定可能帯域 */
    remainingBandwidth(): { value: number; unit: "MBPS" | "GBPS" } {
      const limitBandwidthMbps =
        (this.$filter.bandwidthToBps(this.vnConnectList.bandwidth) ?? 0) /
        1000 /
        1000;
      const sumBandwidthMbps = this.vnConnectList.cloudVnConnectsList.reduce(
        (a, e) =>
          a +
          (this.$filter.bandwidthToBps(e.cloudVnConnectCommon.bandwidth) ?? 0) /
            1000 /
            1000,
        0
      );
      const diffBandwidthMbps = limitBandwidthMbps - sumBandwidthMbps;
      if (diffBandwidthMbps >= 1000 || diffBandwidthMbps <= -1000) {
        return { value: diffBandwidthMbps / 1000, unit: "GBPS" };
      } else {
        return { value: diffBandwidthMbps, unit: "MBPS" };
      }
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** 読み込み */
    async load() {
      this.vnConnectList = await this.$api.cloudVnOracle.getVnConnects(
        this.cloudLineSeq
      );
    },

    /** VNコネクト追加 */
    async register() {
      this.alertMsg = null;
      await this.$modal.show(MultiCloudOracleVnLineEdit, {
        cloudLineSeq: this.cloudLineSeq,
        cloudVnOracleKeyId: this.vnConnectList.cloudVnOracleKeyId,
        remainingBandwidth: this.$filter.bandwidthToBps(
          this.remainingBandwidth
        ),
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** 有効/無効 */
    async modifyStatus(isEnabled: boolean) {
      if (this.vnConnects.selected.length === 0) {
        this.alertMsg = this.$filter.message("no_select_data");
        return;
      } else {
        this.alertMsg = null;
      }

      const wNumbers: string[] = await this.$modal.show(
        MultiCloudVNConnectStatusModify,
        {
          enable: isEnabled,
          vnConnects: this.vnConnects.selected,
        }
      );

      if (isEnabled) {
        await this.$api.cloudVnOracle.putEnableVnConnect({
          cloudVnConnectList: wNumbers,
          cloudVnOracleKeyId: this.vnConnectList.cloudVnOracleKeyId,
        });
      } else {
        await this.$api.cloudVnOracle.putDisableVnConnect({
          cloudVnConnectList: wNumbers,
          cloudVnOracleKeyId: this.vnConnectList.cloudVnOracleKeyId,
        });
      }
      await (
        this.$refs.modal as typeof VueFinalModal & { ok: () => void }
      ).ok();
    },

    /** VNコネクト詳細表示 */
    async showConnectionDetails(entity: CloudVnOracleItem) {
      this.alertMsg = null;
      await this.$modal.show(MultiCloudOracleVnConnectionReference, {
        wnumber: entity.cloudVnConnectAct.wnumber,
        cloudLineSeq: this.cloudLineSeq,
        remainingBandwidth: this.$filter.bandwidthToBps(
          this.remainingBandwidth
        ),
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<CloudVnOracleList>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
